import { HOST } from "../constants/HostConstants";

/**
 * API Service with aggressive caching for cold-start / sleeping APIs.
 *
 * Strategy:
 *  - All fetched data is persisted in localStorage (survives refreshes).
 *  - Cached data is returned immediately so users always see content.
 *  - Fresh data is fetched in the background; if it differs from cache
 *    the caller's setter is invoked so the UI updates seamlessly.
 *  - Cache only expires after 7 days (safety net); in practice the
 *    background refresh keeps it current.
 */

const CACHE_PREFIX = "llave_cache_";
const CACHE_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const KEEP_ALIVE_INTERVAL = 5 * 60 * 1000; // 5 minutes

// Track last keep-alive ping
let lastKeepAlivePing = 0;

/**
 * Get cache key for a request
 */
const getCacheKey = (endpoint, params) => {
  return `${CACHE_PREFIX}${endpoint}_${JSON.stringify(params || {})}`;
};

/**
 * Get cached data if available and not expired.
 * With the 7-day window this almost always returns data when it exists.
 */
export const getCachedData = (endpoint, params) => {
  try {
    const key = getCacheKey(endpoint, params);
    const cached = localStorage.getItem(key);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const isExpired = Date.now() - timestamp > CACHE_EXPIRY_MS;

    return isExpired ? null : data;
  } catch (error) {
    return null;
  }
};

/**
 * Cache response data
 */
const setCachedData = (endpoint, params, data) => {
  try {
    const key = getCacheKey(endpoint, params);
    localStorage.setItem(
      key,
      JSON.stringify({
        data,
        timestamp: Date.now(),
      }),
    );
  } catch (error) {}
};

/**
 * Keep the API awake by pinging a lightweight endpoint
 */
export const keepApiAlive = async () => {
  const now = Date.now();
  if (now - lastKeepAlivePing < KEEP_ALIVE_INTERVAL) {
    return; // Skip if pinged recently
  }

  try {
    lastKeepAlivePing = now;
    // Use a lightweight endpoint that doesn't require auth
    await fetch(`${HOST}/api/property/propertyStats`, {
      method: "GET",
      credentials: "include",
    });
  } catch (error) {
    // Silent fail - API might be waking up
  }
};

/**
 * Fetch with retry and cache support.
 * Always attempts a network request; caches successful responses.
 * Falls back to cache when the API is unreachable.
 */
export const fetchWithCache = async (
  endpoint,
  options = {},
  useCache = true,
) => {
  const {
    method = "GET",
    body,
    params,
    cacheKey: customCacheKey,
    retries = 2,
    timeout = 10000,
  } = options;

  // Use custom cache key if provided, otherwise use endpoint
  const effectiveCacheKey = customCacheKey || endpoint;

  // Keep API alive before making request
  await keepApiAlive();

  let lastError;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      const url =
        method === "GET" && params
          ? `${HOST}${endpoint}?${new URLSearchParams(params)}`
          : `${HOST}${endpoint}`;

      // Handle body: if it's already a string, use it; otherwise stringify
      const processedBody =
        method !== "GET"
          ? typeof body === "string"
            ? body
            : JSON.stringify(body)
          : undefined;

      const response = await fetch(url, {
        method,
        body: processedBody,
        credentials: "include",
        headers: {
          Accept: "application/json, text/plain, */*",
          "Content-Type": "application/json",
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        // Read response body for improved diagnostics
        let errText = "";
        try {
          errText = await response.text();
        } catch (e) {
          errText = "<unable to read response body>";
        }
        throw new Error(
          `HTTP ${response.status}: ${response.statusText} - ${errText}`,
        );
      }

      // Read response body as text first to handle empty bodies gracefully
      const text = await response.text();
      if (!text || text.trim() === "") {
        // No content
        return null;
      }

      let data;
      try {
        data = JSON.parse(text);
      } catch (err) {
        throw new Error(
          `Failed to parse JSON response for ${endpoint}: ${err.message}`,
        );
      }

      // Cache successful responses
      if (useCache) {
        setCachedData(effectiveCacheKey, params, data);
      }

      return data;
    } catch (error) {
      lastError = error;
      const isLastAttempt = attempt === retries;

      if (!isLastAttempt) {
        // Wait before retrying (exponential backoff)
        const delay = Math.min(1000 * Math.pow(2, attempt), 5000);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  // All retries failed, try cache as fallback
  if (useCache) {
    const cached = getCachedData(effectiveCacheKey, params);
    if (cached) {
      return cached;
    }
  }

  // No cache available, throw error
  throw lastError;
};

/**
 * Clear specific cache entry
 */
export const clearCache = (endpoint, params) => {
  try {
    const key = getCacheKey(endpoint, params);
    localStorage.removeItem(key);
  } catch (error) {}
};

/**
 * Clear all Llave cache
 */
export const clearAllCache = () => {
  try {
    const keys = Object.keys(localStorage).filter((key) =>
      key.startsWith(CACHE_PREFIX),
    );
    keys.forEach((key) => localStorage.removeItem(key));
  } catch (error) {}
};

/**
 * Helper for actions: Show cached data immediately, then fetch fresh data
 * This pattern ensures the UI is responsive while data loads in the background
 * @param {string} cacheKey - Unique cache identifier
 * @param {function} fetchFn - Async function that returns fresh data
 * @param {function} setData - React setState function to update UI
 * @returns {Promise<any>} - The fresh data from the API
 */
export const withCache = async (cacheKey, fetchFn, setData) => {
  // Show cached data immediately if available
  let cachedData = null;
  try {
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      const { data } = JSON.parse(cached);
      cachedData = data;
      if (setData && cachedData) {
        setData(cachedData);
      }
    }
  } catch (e) {
    // Ignore parse errors
  }

  try {
    // Fetch fresh data in the background
    const freshData = await fetchFn();

    // Cache the response
    try {
      localStorage.setItem(
        cacheKey,
        JSON.stringify({
          data: freshData,
          timestamp: Date.now(),
        }),
      );
    } catch (e) {
      // Ignore storage errors
    }

    // Update UI with fresh data
    if (setData && freshData) {
      setData(freshData);
    }

    return freshData;
  } catch (error) {
    // If fetch fails, keep showing cached data or empty state
    if (!cachedData && setData) {
      setData([]);
    }
    return cachedData || [];
  }
};

/**
 * Stale-while-revalidate pattern for actions.
 *
 * 1. Returns cached data immediately via `setData` so the page is never empty.
 * 2. Fires a background fetch to the API.
 * 3. When fresh data arrives, compares it to cached data.
 *    - If different → calls `setData` again to refresh the UI and updates cache.
 *    - If the same  → no-op (avoids unnecessary re-renders).
 * 4. If the API is sleeping/unreachable, the cached data stays on screen.
 *
 * @param {string}   endpoint    - API path, e.g. "/api/property/propertyList"
 * @param {object}   options     - Same shape as fetchWithCache options
 * @param {function} setData     - React setState (or similar) to push data to the UI
 * @param {object}   [opts]      - Extra options
 * @param {function} [opts.transform] - Optional transform applied before setData
 * @returns {Promise<any>} The fresh data or cached fallback
 */
export const fetchAndRefresh = async (
  endpoint,
  options = {},
  setData,
  { transform } = {},
) => {
  const { cacheKey: customCacheKey, params } = options;
  const effectiveCacheKey = customCacheKey || endpoint;

  // 1. Show cached data immediately
  const cached = getCachedData(effectiveCacheKey, params);
  if (cached && setData) {
    setData(transform ? transform(cached) : cached);
  }

  // 2. Fetch fresh data from the network
  try {
    const freshData = await fetchWithCache(endpoint, options, true);
    const finalData = transform ? transform(freshData) : freshData;

    // 3. Only re-render if data actually changed
    if (JSON.stringify(freshData) !== JSON.stringify(cached)) {
      if (setData) setData(finalData);
    }

    return finalData;
  } catch (error) {
    // 4. API unreachable — cached data is already displayed
    if (!cached && setData) {
      setData(transform ? transform([]) : []);
    }
    return cached || [];
  }
};

export default {
  getCachedData,
  fetchWithCache,
  fetchAndRefresh,
  keepApiAlive,
  clearCache,
  clearAllCache,
  withCache,
};
